/* ============================================================================
   Product 360 View — location pricing (sticky bar)
   Reads window.variantLocationPrices, resolves the user's state/city, and keeps
   the sticky bar price + state dropdown in sync with the active variant.
   Listens to `p360:variantchange` fired by <product-360-view> and <product-360-booking>.
   ========================================================================== */

(function () {
  const wrapper = document.querySelector('.p360-price-wrapper');
  if (!wrapper) return;

  const priceEl = wrapper.querySelector('.p360-dynamic-price');
  const locationEl = wrapper.querySelector('.p360-dynamic-location');
  const priceText = wrapper.querySelector('.p360-price-text');
  const stateDropdowns = wrapper.querySelectorAll('.p360-state-selector');
  const defaultPrice = wrapper.dataset.defaultPrice || '';

  const STORAGE_KEY = 'jawa_user_location';
  const cityAliases = {
    Bengaluru: 'Bangalore',
    'Bangalore Urban': 'Bangalore',
    Bombay: 'Mumbai',
    Madras: 'Chennai',
    Trivandrum: 'Thiruvananthapuram',
  };

  // Clear legacy stored selected state so products always start fresh with detected location
  try {
    localStorage.removeItem('jawa_user_selected_state');
  } catch (e) {}

  let cachedLocation = null;
  // In-memory activeState: persists ONLY across variants of the current product
  let activeState = null;

  function getCurrentVariantId() {
    return (
      document.querySelector('.p360__swatch.active')?.dataset?.variantId ||
      document.querySelector('.p360__swatch.active')?.id ||
      document.querySelector('.p360-sticky__swatch.active')?.dataset?.variantId ||
      document.querySelector('.p360-sticky__swatch.active')?.id ||
      document.querySelector('.p360__swatch')?.dataset?.variantId ||
      document.querySelector('.p360__swatch')?.id ||
      document.querySelector('.p360-sticky__swatch')?.dataset?.variantId ||
      document.querySelector('.p360-sticky__swatch')?.id ||
      null
    );
  }

  let currentVariantId = getCurrentVariantId();

  /* ------------------------------ helpers ------------------------------- */

  function setLoading(on) {
    wrapper.classList.toggle('loading', on);
    if (priceText) priceText.classList.toggle('loading', on);
  }

  function renderPrice(price) {
    if (priceEl) priceEl.textContent = price || '—';
    if (locationEl) locationEl.textContent = 'Ex-Showroom Price*';
  }

  function getUserLocation() {
    if (cachedLocation) return Promise.resolve(cachedLocation);

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        cachedLocation = JSON.parse(stored);
        return Promise.resolve(cachedLocation);
      } catch (e) {
        /* ignore malformed cache */
      }
    }

    return new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude: lat, longitude: lon } = pos.coords;
            const res = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
            );
            const data = await res.json();

            let city =
              data.city ||
              data.locality ||
              data.localityInfo?.administrative?.[2]?.name ||
              '';
            const state = data.principalSubdivision || '';
            city = cityAliases[city] || city;

            cachedLocation = { city, state };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cachedLocation));
            resolve(cachedLocation);
          } catch (e) {
            resolve(null);
          }
        },
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  }

  function populateStates(variantData, selectedState) {
    if (!stateDropdowns.length) return;
    stateDropdowns.forEach((dropdown) => {
      dropdown.innerHTML = '';
      Object.keys(variantData.states || {})
        .sort()
        .forEach((state) => {
          const opt = document.createElement('option');
          opt.value = state;
          opt.textContent = state;
          if (state === selectedState) opt.selected = true;
          dropdown.appendChild(opt);
        });
      if (selectedState) {
        dropdown.value = selectedState;
      }
    });
  }

  /* ------------------------------ core ---------------------------------- */

  async function updateForVariant(variantId) {
    const variantData = window.variantLocationPrices?.[variantId];

    // No pricing map for this variant -> fall back to the default price.
    if (!variantData || typeof variantData !== 'object') {
      renderPrice(defaultPrice);
      setLoading(false);
      return;
    }

    setLoading(true);
    if (priceEl) priceEl.textContent = 'Loading…';

    const availableStates = Object.keys(variantData.states || {});

    // Determine target state:
    // 1. In-memory activeState (set when user changes dropdown on THIS product)
    let targetState = activeState;

    // 2. Geolocation fallback
    const location = await getUserLocation();

    // Guard against race conditions if another variant was selected while awaiting location
    if (variantId !== currentVariantId) return;

    if (!targetState && location && location.state) {
      targetState = location.state;
    }

    // 3. Default location fallback (e.g. 'Delhi')
    if (!targetState) {
      targetState = variantData.default?.location || 'Delhi';
    }

    // Make sure the selected state actually exists in the data set.
    if (!availableStates.includes(targetState)) {
      targetState = availableStates.includes('Delhi')
        ? 'Delhi'
        : availableStates[0] || '';
    }

    // Retain this state for subsequent variant switches of the same product
    activeState = targetState;

    // Resolve price for targetState
    let finalPrice = '';
    const stateData = targetState ? variantData.states?.[targetState] : null;

    if (stateData) {
      if (
        location &&
        location.state === targetState &&
        location.city &&
        stateData[location.city]?.price
      ) {
        finalPrice = stateData[location.city].price;
      } else if (stateData.price) {
        finalPrice = stateData.price;
      }
    }

    if (!finalPrice) {
      finalPrice = variantData.default?.price || defaultPrice;
    }

    populateStates(variantData, targetState);
    renderPrice(finalPrice);
    setLoading(false);
  }

  /* ------------------------------ init ---------------------------------- */

  setLoading(true);
  if (priceEl) priceEl.textContent = 'Loading…';

  setTimeout(() => {
    currentVariantId = currentVariantId || getCurrentVariantId();
    if (currentVariantId) updateForVariant(currentVariantId);
    else {
      renderPrice(defaultPrice);
      setLoading(false);
    }
  }, 300);

  /* ----------------------------- events --------------------------------- */

  // Variant change dispatched by <product-360-view> or <product-360-booking>.
  document.addEventListener('p360:variantchange', (e) => {
    const id = e.detail?.variantId;
    const isProductChange = !!e.detail?.isProductChange;
    if (!id) return;

    // If switching product, reset activeState so it re-detects location or uses default
    if (isProductChange) {
      activeState = null;
    } else if (id === currentVariantId) {
      return;
    }

    currentVariantId = id;
    updateForVariant(id);
  });

  // Explicit product change event
  document.addEventListener('p360:productchange', () => {
    activeState = null;
  });

  // Manual state change from the dropdown (synced across desktop & mobile).
  stateDropdowns.forEach((dropdown) => {
    dropdown.addEventListener('change', (e) => {
      const selectedVal = e.target.value;
      if (!selectedVal) return;

      activeState = selectedVal;

      stateDropdowns.forEach((d) => {
        d.value = selectedVal;
      });

      const variantData = window.variantLocationPrices?.[currentVariantId];
      if (!variantData) return;

      const stateData = variantData.states?.[selectedVal];
      let price = '';
      if (stateData) {
        if (
          cachedLocation &&
          cachedLocation.state === selectedVal &&
          cachedLocation.city &&
          stateData[cachedLocation.city]?.price
        ) {
          price = stateData[cachedLocation.city].price;
        } else if (stateData.price) {
          price = stateData.price;
        }
      }

      renderPrice(price || variantData.default?.price || defaultPrice);
    });
  });
})();
